import { useState, useEffect, useRef } from 'react';
import { Plus, ShoppingCart, Briefcase, Tv, List, Check, Trash2, RefreshCw, X, Eye, EyeOff, Menu, Settings } from 'lucide-react';
import { useLists, useList, useAddListItem, useUpdateListItem, useDeleteListItem, useClearCheckedItems, useCreateList, useKeepStatus, useSyncAllKeep, useDisplaySettings } from '../api/hooks';
import { trackEvent } from '../lib/analytics';

const KEEP_SYNC_COOLDOWN_MS = 30000; // 30 seconds

const iconMap = {
  groceries: ShoppingCart,
  packing: Briefcase,
  watch: Tv,
  custom: List,
};

const defaultColors: Record<string, string> = {
  groceries: '#6fb1a0', // Sage
  packing: '#15a2a2',   // Teal
  watch: '#1184a7',     // Teal Dark
  custom: '#fe7434',    // Orange
};

export function ListsPage() {
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [newItemTitle, setNewItemTitle] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListType, setNewListType] = useState<'groceries' | 'packing' | 'watch' | 'custom'>('custom');
  const [showCompleted, setShowCompleted] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const settingsMenuRef = useRef<HTMLDivElement>(null);
  const [syncingItemIds, setSyncingItemIds] = useState<Set<string>>(new Set());

  const { data: lists, isLoading: listsLoading } = useLists();
  const { data: selectedList, isLoading: listLoading } = useList(selectedListId || '');
  const { data: displaySettings } = useDisplaySettings();
  const addItem = useAddListItem();
  const updateItem = useUpdateListItem();
  const deleteItem = useDeleteListItem();
  const clearChecked = useClearCheckedItems();
  const createList = useCreateList();
  const { data: keepStatus } = useKeepStatus();
  const syncAllKeep = useSyncAllKeep();

  // Trigger Keep sync when page loads (with 30s cooldown)
  useEffect(() => {
    if (!keepStatus?.connected) return;

    const lastSync = localStorage.getItem('keep_last_sync');
    const now = Date.now();

    if (!lastSync || now - parseInt(lastSync) > KEEP_SYNC_COOLDOWN_MS) {
      syncAllKeep.mutate();
      localStorage.setItem('keep_last_sync', now.toString());
    }
  }, [keepStatus?.connected]);

  // Auto-select default list on mount
  useEffect(() => {
    if (lists && lists.length > 0 && !selectedListId) {
      const defaultId = displaySettings?.defaultListId;
      // Use configured default if it exists in the list, otherwise first list
      const listToSelect = defaultId && lists.find(l => l.id === defaultId)
        ? defaultId
        : lists[0]!.id;
      setSelectedListId(listToSelect);
    }
  }, [lists, displaySettings?.defaultListId, selectedListId]);

  // Handle list deletion - if selected list was deleted, select first available
  useEffect(() => {
    if (selectedListId && lists && lists.length > 0) {
      const selectedExists = lists.some(l => l.id === selectedListId);
      if (!selectedExists) {
        setSelectedListId(lists[0]!.id);
      }
    }
  }, [lists, selectedListId]);

  // Handle click outside settings menu
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target as Node)) {
        setShowSettingsMenu(false);
      }
    }

    if (showSettingsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSettingsMenu]);

  const handleAddItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemTitle.trim() || !selectedListId) return;

    addItem.mutate(
      { listId: selectedListId, title: newItemTitle.trim() },
      {
        onSuccess: (data) => {
          // Briefly mark real ID as syncing to show completion animation
          if (data?.id) {
            setSyncingItemIds(prev => new Set(prev).add(data.id));

            // Clear syncing state after brief delay
            setTimeout(() => {
              setSyncingItemIds(prev => {
                const next = new Set(prev);
                next.delete(data.id);
                return next;
              });
            }, 500);
          }
        }
      }
    );

    setNewItemTitle('');
  };

  const handleToggleItem = (itemId: string, checked: boolean) => {
    // Mark as syncing
    setSyncingItemIds(prev => new Set(prev).add(itemId));

    updateItem.mutate(
      { id: itemId, checked: !checked },
      {
        onSuccess: () => {
          // Keep syncing state briefly to show completion animation
          setTimeout(() => {
            setSyncingItemIds(prev => {
              const next = new Set(prev);
              next.delete(itemId);
              return next;
            });
          }, 500);
        },
        onError: () => {
          // Remove syncing state immediately on error
          setSyncingItemIds(prev => {
            const next = new Set(prev);
            next.delete(itemId);
            return next;
          });
        }
      }
    );
  };

  const handleCreateList = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newListName.trim()) return;
    createList.mutate({
      name: newListName.trim(),
      type: newListType,
      color: defaultColors[newListType],
    }, {
      onSuccess: (newList) => {
        setNewListName('');
        setNewListType('custom');
        setShowCreateModal(false);
        // Select the newly created list
        if (newList?.id) {
          setSelectedListId(newList.id);
        }
      },
    });
  };

  const handleSelectList = (listId: string) => {
    setSelectedListId(listId);
    setShowCompleted(false); // Reset show completed when switching lists
    setSidebarOpen(false); // Close sidebar on mobile
  };

  // Get the current selected list color
  const selectedListColor = selectedList?.color || defaultColors[selectedList?.type || 'custom'] || '#fe7434';
  const SelectedIcon = selectedList ? (iconMap[selectedList.type] || List) : List;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-bg-surface border-b border-border-default px-4 md:px-6 py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button
              className="md:hidden btn-icon"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="font-display text-display-sm font-semibold text-text-primary">Lists</h1>
          </div>
          <button onClick={() => setShowCreateModal(true)} className="btn btn-primary rounded-xl shadow-teal hover:shadow-lg transition-all duration-200">
            <Plus className="w-5 h-5 md:mr-2" />
            <span className="hidden md:inline">New List</span>
          </button>
        </div>
      </div>

      {/* Main Content: Sidebar + Detail */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Mobile Backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div
          className={`
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            md:translate-x-0 md:relative
            fixed inset-y-0 left-0 z-40
            w-72 bg-bg-surface border-r border-border-default
            flex flex-col
            transition-transform duration-200 ease-in-out
            md:transition-none
          `}
        >
          {/* Sidebar Header (mobile only) */}
          <div className="md:hidden flex items-center justify-between px-4 py-4 border-b border-border-light">
            <span className="font-display font-semibold text-text-primary">Your Lists</span>
            <button
              onClick={() => setSidebarOpen(false)}
              className="btn-icon"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* List Tiles */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {listsLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-teal" />
              </div>
            ) : !lists || lists.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 rounded-2xl bg-teal/10 flex items-center justify-center mx-auto mb-3">
                  <List className="w-7 h-7 text-teal" />
                </div>
                <p className="text-sm font-medium text-text-secondary">No lists yet</p>
              </div>
            ) : (
              lists.map((list) => {
                const Icon = iconMap[list.type] || List;
                const remaining = list.itemCount - list.checkedCount;
                const color = list.color || defaultColors[list.type] || '#fe7434';
                const isSelected = selectedListId === list.id;

                return (
                  <button
                    key={list.id}
                    onClick={() => handleSelectList(list.id)}
                    className={`
                      w-full p-4 rounded-xl border-2 transition-all duration-200 text-left
                      ${isSelected
                        ? 'bg-teal/5 border-teal shadow-sm'
                        : 'border-border-light hover:bg-bg-secondary hover:border-border-default hover:shadow-sm'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${color}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-text-primary truncate">
                          {list.name}
                        </div>
                        <div className="text-xs text-text-muted mt-0.5">
                          {remaining} item{remaining !== 1 ? 's' : ''} remaining
                        </div>
                      </div>
                      {remaining > 0 && (
                        <div
                          className="text-xl font-bold flex-shrink-0"
                          style={{ color }}
                        >
                          {remaining}
                        </div>
                      )}
                    </div>

                    {/* Progress bar */}
                    {list.itemCount > 0 && (
                      <div className="mt-3 h-1.5 bg-bg-secondary rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-300"
                          style={{
                            width: `${(list.checkedCount / list.itemCount) * 100}%`,
                            backgroundColor: color,
                          }}
                        />
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* New List Button (Sidebar) */}
          <div className="p-4 border-t border-border-light">
            <button
              onClick={() => {
                setShowCreateModal(true);
                setSidebarOpen(false);
              }}
              className="w-full p-4 rounded-xl border-2 border-dashed border-border-default hover:border-teal hover:bg-teal/5 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5 text-teal" />
              <span className="text-text-secondary font-medium">New List</span>
            </button>
          </div>
        </div>

        {/* Detail Panel */}
        <div className="flex-1 flex flex-col overflow-hidden bg-white">
          {!lists || lists.length === 0 ? (
            // No lists - show create prompt
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-20 h-20 rounded-2xl bg-teal/10 flex items-center justify-center mx-auto mb-4">
                  <List className="w-10 h-10 text-teal" />
                </div>
                <h3 className="font-display text-xl font-semibold text-text-primary mb-2">No lists yet</h3>
                <p className="text-text-secondary mb-6">Create your first list to get started</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="btn btn-primary rounded-xl px-6"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create List
                </button>
              </div>
            </div>
          ) : selectedList ? (
            // Show selected list detail
            <>
              {/* List Header */}
              <div className="px-4 md:px-6 py-5 border-b border-border-light bg-bg-secondary/30">
                <div className="flex items-center gap-4">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm"
                    style={{ backgroundColor: `${selectedListColor}15` }}
                  >
                    <SelectedIcon className="w-7 h-7" style={{ color: selectedListColor }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-display text-xl font-semibold text-text-primary truncate">
                      {selectedList.name}
                    </h2>
                    <p className="text-sm text-text-secondary mt-0.5">
                      {selectedList.itemCount - selectedList.checkedCount} item{selectedList.itemCount - selectedList.checkedCount !== 1 ? 's' : ''} remaining
                    </p>
                  </div>
                  {selectedList.checkedCount > 0 && (
                    <div className="relative" ref={settingsMenuRef}>
                      <button
                        onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                        className="btn btn-secondary text-sm rounded-xl"
                        title="List settings"
                      >
                        <Settings className="w-4 h-4" />
                      </button>

                      {showSettingsMenu && (
                        <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-elevated border border-border-light z-50 py-2">
                          <button
                            onClick={() => {
                              setShowCompleted(!showCompleted);
                              setShowSettingsMenu(false);
                              trackEvent('list_toggle_completed_visibility', {
                                list_id: selectedListId,
                                show_completed: !showCompleted,
                              });
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-bg-secondary transition-colors flex items-center gap-3"
                          >
                            {showCompleted ? (
                              <>
                                <EyeOff className="w-4 h-4 text-text-secondary" />
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-text-primary">Hide Completed</div>
                                  <div className="text-xs text-text-muted">Hide {selectedList.checkedCount} done item{selectedList.checkedCount !== 1 ? 's' : ''}</div>
                                </div>
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4 text-text-secondary" />
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-text-primary">Show Completed</div>
                                  <div className="text-xs text-text-muted">Show {selectedList.checkedCount} done item{selectedList.checkedCount !== 1 ? 's' : ''}</div>
                                </div>
                              </>
                            )}
                          </button>

                          <div className="h-px bg-border-light my-1" />

                          <button
                            onClick={() => {
                              clearChecked.mutate(selectedListId!);
                              setShowSettingsMenu(false);
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-coral/5 transition-colors flex items-center gap-3"
                          >
                            <Trash2 className="w-4 h-4 text-coral" />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-coral">Clear Completed</div>
                              <div className="text-xs text-text-muted">Permanently delete {selectedList.checkedCount} item{selectedList.checkedCount !== 1 ? 's' : ''}</div>
                            </div>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Add Item Form */}
              <div className="px-4 md:px-6 py-5 border-b border-border-light">
                <form onSubmit={handleAddItem}>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={newItemTitle}
                      onChange={(e) => setNewItemTitle(e.target.value)}
                      placeholder="Add an item..."
                      data-testid="add-item-input"
                      className="flex-1 px-4 py-3 border-2 border-border-default rounded-xl focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal transition-all duration-200"
                    />
                    <button
                      type="submit"
                      disabled={!newItemTitle.trim() || addItem.isPending}
                      className="btn btn-primary px-5 rounded-xl"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </form>
              </div>

              {/* Items List */}
              <div className="flex-1 overflow-y-auto px-4 md:px-6 py-5" data-testid="lists-container">
                {(() => {
                  const filteredItems = showCompleted
                    ? selectedList.items
                    : selectedList.items.filter(item => !item.checked);
                  const hiddenCount = selectedList.items.length - filteredItems.length;

                  if (listLoading) {
                    return (
                      <div className="flex items-center justify-center py-12">
                        <RefreshCw className="w-8 h-8 animate-spin text-teal" />
                      </div>
                    );
                  }

                  if (selectedList.items.length === 0) {
                    return (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 rounded-2xl bg-teal/10 flex items-center justify-center mx-auto mb-4">
                          <List className="w-8 h-8 text-teal" />
                        </div>
                        <p className="text-text-secondary font-medium">No items yet. Add one above!</p>
                      </div>
                    );
                  }

                  if (filteredItems.length === 0 && hiddenCount > 0) {
                    return (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 rounded-2xl bg-sage/20 flex items-center justify-center mx-auto mb-4">
                          <Check className="w-8 h-8 text-sage" />
                        </div>
                        <p className="font-display text-lg font-semibold text-text-primary">All done!</p>
                        <p className="text-text-muted text-sm mt-1">
                          {hiddenCount} completed item{hiddenCount !== 1 ? 's' : ''} hidden
                        </p>
                        <button
                          onClick={() => setShowCompleted(true)}
                          className="btn btn-secondary text-sm mt-4 rounded-xl"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Show Completed
                        </button>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-3">
                      {filteredItems.map((item) => {
                        // Check if syncing: either tracked explicitly or is a temp item
                        const isSyncing = syncingItemIds.has(item.id) || item.id.startsWith('temp-');
                        return (
                          <div
                            key={item.id}
                            data-testid="list-item"
                            className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 ${
                              isSyncing ? 'opacity-70' : ''
                            } ${
                              item.checked ? 'bg-bg-secondary/50 border-border-light' : 'bg-white border-border-light hover:border-border-default hover:shadow-sm'
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => handleToggleItem(item.id, item.checked)}
                              disabled={isSyncing}
                              className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all duration-200 flex-shrink-0 ${
                                item.checked
                                  ? 'border-sage bg-sage text-white'
                                  : 'border-border-default hover:border-teal hover:bg-teal/5'
                              } ${
                                isSyncing ? 'cursor-wait' : 'cursor-pointer'
                              }`}
                            >
                              {item.checked && <Check className="w-4 h-4" />}
                            </button>
                            <span className={`flex-1 font-medium ${item.checked ? 'line-through text-text-muted' : 'text-text-primary'}`}>
                              {item.title}
                            </span>
                            <button
                              type="button"
                              onClick={() => deleteItem.mutate(item.id)}
                              disabled={isSyncing}
                              className={`text-text-muted hover:text-coral transition-colors flex-shrink-0 p-1 rounded-lg hover:bg-coral/10 ${
                                isSyncing ? 'pointer-events-none opacity-50' : ''
                              }`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </>
          ) : (
            // Loading or no selection
            <div className="flex-1 flex items-center justify-center">
              <RefreshCw className="w-8 h-8 animate-spin text-text-muted" />
            </div>
          )}
        </div>
      </div>

      {/* Create List Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-elevated w-full max-w-md mx-4 animate-fadeIn">
            <div className="flex items-center justify-between px-6 py-5 border-b border-border-light">
              <h2 className="font-display text-xl font-semibold text-text-primary">Create New List</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-text-muted hover:text-text-primary transition-colors p-1 rounded-lg hover:bg-bg-secondary">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateList} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-text-secondary mb-2">List Name</label>
                <input
                  type="text"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="e.g., Groceries, Vacation Packing"
                  className="w-full px-4 py-3 border-2 border-border-default rounded-xl focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal transition-all duration-200"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-text-secondary mb-2">List Type</label>
                <div className="grid grid-cols-4 gap-3">
                  {(['groceries', 'packing', 'watch', 'custom'] as const).map((type) => {
                    const Icon = iconMap[type];
                    const color = defaultColors[type];
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setNewListType(type)}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                          newListType === type
                            ? 'border-teal bg-teal/10 shadow-sm'
                            : 'border-border-light hover:border-border-default hover:bg-bg-secondary'
                        }`}
                      >
                        <Icon className="w-6 h-6 mx-auto" style={{ color }} />
                        <span className="text-xs font-medium text-text-secondary block mt-2 capitalize">{type}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 btn btn-secondary rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newListName.trim() || createList.isPending}
                  className="flex-1 btn btn-primary rounded-xl"
                >
                  Create List
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
